import React, { useState, useMemo } from "react";
import "./activeCompsTab.css";
import MarketControlBar from "./MarketControlBar";
import MobileFiltersDrawer from "./MobileFiltersDrawer";
import SummaryStatsRow from "./SummaryStatsRow";
import CompRowCard from "./CompRowCard";
import VehicleSummaryCard from "./VehicleSummaryCard";

export default function SoldCompsTab({ data, isMobile }) {
  if (!data) return null;

  const [selectedComp, setSelectedComp] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("90D");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const defaultFilters = {
    sortBy: "soldDate",
    sortOrder: "desc",
    mileageRange: [0, 500000],
    similarMileage: false,
    radius: 100,
    independentDealersOnly: false,
    franchiseOnly: false,
    matchingVersionOnly: false,
    matchingTransmissionOnly: false,
    matchingDrivetrainOnly: false,
  };
  const [filters, setFilters] = useState(defaultFilters);

  const metrics = data?.market_analysis?.market_metrics || {};
  const vehicleInfo = data?.vehicle_info || {};
  const pricingInfo = data?.pricing_info || {};
  const searchMetadata = data?.search_metadata || {};
  const hooptieMileage = searchMetadata?.mileage || vehicleInfo?.mileage || 0;
  const hooptiePrice = pricingInfo?.marketcheck_price || pricingInfo?.hooptie_price || searchMetadata?.asking_price || 0;
  // Exact vehicle drivetrain/transmission from vehicle_info (with fallbacks for alternate API keys)
  const hooptieDrivetrain = vehicleInfo?.drivetrain ?? vehicleInfo?.drivetrain_type ?? searchMetadata?.drivetrain ?? null;
  const hooptieTransmission = vehicleInfo?.transmission ?? vehicleInfo?.transmission_type ?? searchMetadata?.transmission ?? null;

  const recentSales = data?.market_analysis?.recent_sales || [];

  // Transform recent_sales into comp format (include listings without price or mileage)
  const allComps = useMemo(() => {
    return recentSales.map((sale, index) => {
        const salePrice = sale.price ?? null;
        const saleMiles = sale.miles ?? sale.mileage ?? null;
        // Compare against Hooptie's price (use 0 delta when no price)
        const priceDelta = salePrice != null ? salePrice - hooptiePrice : 0;
        const priceDeltaType = priceDelta < -500 ? "under" : priceDelta > 500 ? "over" : "market";
        // Compare against Hooptie's mileage (use 0 delta when no mileage)
        const mileageDelta = saleMiles != null ? saleMiles - hooptieMileage : 0;
        const mileageDeltaType = mileageDelta > 0 ? "over" : "under";
        
        // Determine status
        let status = "Sold";
        
        // Calculate comp quality (1-5); use safe defaults when price or mileage missing
        const safePrice = hooptiePrice || 1;
        const safeMileage = hooptieMileage || 1;
        const priceDiffPercent = salePrice != null ? Math.abs(priceDelta) / safePrice : 0;
        const mileageDiffPercent = saleMiles != null ? Math.abs(mileageDelta) / safeMileage : 0;
        const totalDiff = (priceDiffPercent + mileageDiffPercent) / 2;
        let compQuality = 5;
        if (totalDiff > 0.3) compQuality = 1;
        else if (totalDiff > 0.2) compQuality = 2;
        else if (totalDiff > 0.1) compQuality = 3;
        else if (totalDiff > 0.05) compQuality = 4;
        
        const qualityLevel = compQuality <= 2 ? "LOW" : compQuality <= 3 ? "MED" : "HIGH";
        
        return {
          id: sale.vin || `sold-${index}`,
          image: sale.photo || null, // Only use listing photo; show "Image not available" when missing or load fails
          title: sale.heading || `${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model} ${vehicleInfo.trim}`,
          price: salePrice,
          dealer: sale.dealer_name,
          priceDelta: Math.abs(priceDelta),
          priceDeltaType,
          mileageDelta: Math.abs(mileageDelta),
          mileageDeltaType,
          status,
          avgPrice: Math.round(metrics.avg_sold_price),
          avgDom: metrics.avg_days_on_market,
          compQuality,
          qualityLevel,
          dom: sale.dom_active, // Sold DOM
          mileage: saleMiles,
          profit: metrics?.avg_sold_price != null ? Math.round(metrics.avg_sold_price - sale.price) : 0,
          priceBand: (metrics?.price_range_sold?.min != null && metrics?.price_range_sold?.max != null)
            ? `$${(metrics.price_range_sold.min / 1000).toFixed(1)}k - $${(metrics.price_range_sold.max / 1000).toFixed(1)}k`
            : '—',
          vin: sale.vin,
          dealer_type: sale.dealer_type,
          version: sale.version,
          url: sale.vdp_url,
          soldDate: sale.last_seen_at_date,
          lastSeenAtDate: sale.last_seen_at_date,
          distance_miles: sale.distance_miles ?? sale.distance_mi ?? sale.distance ?? null,
          city: sale.city ?? sale.dealer_city ?? null,
          state: sale.state ?? sale.dealer_state ?? null,
          transmission: sale.transmission ?? null,
          drivetrain: sale.drivetrain ?? null,
        };
      });
  }, [metrics, vehicleInfo, hooptiePrice, hooptieMileage, recentSales]);

  const handleFilterChange = (newFilters) => {
    if (newFilters.similarMileage && !filters.similarMileage) {
      setFilters({ ...newFilters });
    } else if (!newFilters.similarMileage && filters.similarMileage) {
      const allMileages = allComps.map(comp => comp.mileage).filter(m => m != null && m > 0);
      const minMileageBound = allMileages.length > 0 ? Math.min(...allMileages) : 0;
      const maxMileageBound = allMileages.length > 0 ? Math.max(...allMileages, 500000) : 500000;
      setFilters({
        ...newFilters,
        mileageRange: [minMileageBound, maxMileageBound]
      });
    } else {
      setFilters(newFilters);
    }
  };

  const handleCompSelect = (comp) => {
    setSelectedComp({
      title: comp.title,
      image: comp.image,
      price: comp.price,
      mileage: comp.mileage,
      vin: comp.vin,
      dom: comp.dom,
      dealer: comp.dealer,
      profit: comp.profit,
      avgDom: comp.avgDom,
      avgPrice: comp.avgPrice,
      priceBand: comp.priceBand,
      soldDate: comp.soldDate
    });
  };

  // Apply filters and sorting
  const filteredComps = useMemo(() => {
    let filtered = [...allComps];

    const radiusMi = filters.radius != null ? filters.radius : 100;
    filtered = filtered.filter(comp => {
      const d = comp.distance_miles;
      return d == null || d <= radiusMi;
    });

    // Time period filter - filter by last_seen_at_date
    const now = new Date();
    let daysAgo = 30;
    if (selectedPeriod === "60D") daysAgo = 60;
    else if (selectedPeriod === "90D") daysAgo = 90;
    
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    filtered = filtered.filter(comp => {
      if (!comp.lastSeenAtDate) return false;
      const compDate = new Date(comp.lastSeenAtDate);
      return compDate >= cutoffDate;
    });

    // Mileage filter; include comps with no mileage so they still show
    if (filters.similarMileage) {
      const [minMileage, maxMileage] = filters.mileageRange;
      filtered = filtered.filter(comp => {
        const compMileage = comp.mileage;
        if (compMileage == null) return true;
        return compMileage >= minMileage && compMileage <= maxMileage;
      });
    } else {
      filtered = filtered.filter(comp => {
        const compMileage = comp.mileage;
        if (compMileage == null) return true;
        const [min, max] = filters.mileageRange;
        return compMileage >= min && compMileage <= max;
      });
    }

    if (filters.independentDealersOnly) {
      filtered = filtered.filter(comp => comp.dealer_type === "independent");
    }
    if (filters.franchiseOnly) {
      filtered = filtered.filter(comp => comp.dealer_type === "franchise");
    }

    if (filters.matchingVersionOnly) {
      filtered = filtered.filter(comp => comp.version === vehicleInfo.version);
    }

    // Matching Transmission Only filter — compare against exact vehicle's transmission from vehicle_info
    if (filters.matchingTransmissionOnly && hooptieTransmission) {
      const target = String(hooptieTransmission).trim().toLowerCase();
      filtered = filtered.filter(comp => comp.transmission && String(comp.transmission).trim().toLowerCase() === target);
    }

    // Matching Drivetrain Only filter — compare against exact vehicle's drivetrain from vehicle_info
    if (filters.matchingDrivetrainOnly && hooptieDrivetrain) {
      const target = String(hooptieDrivetrain).trim().toLowerCase();
      filtered = filtered.filter(comp => comp.drivetrain && String(comp.drivetrain).trim().toLowerCase() === target);
    }

    // Sorting
    filtered.sort((a, b) => {
      if (filters.sortBy === "soldDate") {
        // Sort by last_seen_at_date
        if (!a.lastSeenAtDate && !b.lastSeenAtDate) return 0;
        if (!a.lastSeenAtDate) return 1; // Put items without date at the end
        if (!b.lastSeenAtDate) return -1;
        
        const aDate = new Date(a.lastSeenAtDate).getTime();
        const bDate = new Date(b.lastSeenAtDate).getTime();
        
        // Handle invalid dates
        if (isNaN(aDate) && isNaN(bDate)) return 0;
        if (isNaN(aDate)) return 1;
        if (isNaN(bDate)) return -1;
        
        if (filters.sortOrder === "asc") {
          return aDate - bDate; // Ascending (oldest first)
        } else {
          return bDate - aDate; // Descending (newest first)
        }
      } else if (filters.sortBy === "price") {
        const aVal = a.price ?? (filters.sortOrder === "asc" ? Infinity : -Infinity);
        const bVal = b.price ?? (filters.sortOrder === "asc" ? Infinity : -Infinity);
        if (filters.sortOrder === "asc") {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      } else if (filters.sortBy === "mileage") {
        const aVal = a.mileage ?? (filters.sortOrder === "asc" ? Infinity : -Infinity);
        const bVal = b.mileage ?? (filters.sortOrder === "asc" ? Infinity : -Infinity);
        if (filters.sortOrder === "asc") {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      } else if (filters.sortBy === "distance") {
        const aVal = a.distance_miles ?? (filters.sortOrder === "asc" ? Infinity : -Infinity);
        const bVal = b.distance_miles ?? (filters.sortOrder === "asc" ? Infinity : -Infinity);
        if (filters.sortOrder === "asc") {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      } else {
        // Fallback: Sort by last_seen_at_date descending (newest first)
        if (!a.lastSeenAtDate && !b.lastSeenAtDate) return 0;
        if (!a.lastSeenAtDate) return 1;
        if (!b.lastSeenAtDate) return -1;
        
        const aDate = new Date(a.lastSeenAtDate).getTime();
        const bDate = new Date(b.lastSeenAtDate).getTime();
        
        if (isNaN(aDate) && isNaN(bDate)) return 0;
        if (isNaN(aDate)) return 1;
        if (isNaN(bDate)) return -1;
        
        return bDate - aDate; // Descending order (newest first)
      }
    });

    return filtered;
  }, [allComps, filters, vehicleInfo, hooptieMileage, hooptieDrivetrain, hooptieTransmission, selectedPeriod]);

  // Calculate stats from filtered comps; exclude missing price/mileage from averages
  const summaryStats = React.useMemo(() => {
    if (filteredComps.length === 0) {
      return {
        soldListings: 0,
        avgPrice: 0,
        avgMileage: 0,
        avgSoldDom: 0
      };
    }

    const compsWithPrice = filteredComps.filter(comp => comp.price != null && comp.price > 0);
    const compsWithMileage = filteredComps.filter(comp => comp.mileage != null && comp.mileage > 0);
    const totalPrice = compsWithPrice.reduce((sum, comp) => sum + comp.price, 0);
    const totalMileage = compsWithMileage.reduce((sum, comp) => sum + comp.mileage, 0);
    const totalDom = filteredComps.reduce((sum, comp) => sum + (comp.dom || 0), 0);
    const avgPrice = compsWithPrice.length > 0 ? Math.round(totalPrice / compsWithPrice.length) : 0;
    const avgMileage = compsWithMileage.length > 0 ? Math.round(totalMileage / compsWithMileage.length) : 0;
    const avgSoldDom = Math.round(totalDom / filteredComps.length);

    return {
      soldListings: filteredComps.length,
      avgPrice: avgPrice,
      avgMileage: avgMileage,
      avgSoldDom: avgSoldDom
    };
  }, [filteredComps]);

  const activeFilterCount = [filters.similarMileage, filters.independentDealersOnly, filters.franchiseOnly, filters.matchingVersionOnly, filters.matchingTransmissionOnly, filters.matchingDrivetrainOnly].filter(Boolean).length;
  const sortLabel = filters.sortBy === "soldDate" ? "Sold Date" : filters.sortBy === "price" ? "Price" : filters.sortBy === "mileage" ? "Mileage" : filters.sortBy === "distance" ? "Distance" : "Sold Date";
  const sortArrow = filters.sortOrder === "asc" ? "↑" : "↓";

  if (isMobile) {
    return (
      <div className="active-comps-tab active-comps-tab-mobile">
        <div className="act-mobile-pills">
          <button type="button" className="act-mobile-pill act-mobile-pill-filters" onClick={() => setMobileFiltersOpen(true)}>
            Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
          </button>
          <button type="button" className="act-mobile-pill act-mobile-pill-sort" onClick={() => handleFilterChange({ ...filters, sortOrder: filters.sortOrder === "asc" ? "desc" : "asc" })}>
            Sort: {sortLabel} {sortArrow}
          </button>
        </div>
        <MobileFiltersDrawer
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          hooptieMileage={hooptieMileage}
          filteredCount={summaryStats.soldListings}
          defaultFilters={defaultFilters}
          isSoldComps={true}
        />
        <div className="ms-period-filter act-mobile-period">
          <div className="ms-period-label">Time Period:</div>
          <div className="ms-period-buttons">
            <button
              className={`ms-period-button ${selectedPeriod === "30D" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("30D")}
            >
              30D
            </button>
            <button
              className={`ms-period-button ${selectedPeriod === "60D" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("60D")}
            >
              60D
            </button>
            <button
              className={`ms-period-button ${selectedPeriod === "90D" ? "active" : ""}`}
              onClick={() => setSelectedPeriod("90D")}
            >
              90D
            </button>
          </div>
        </div>
        <div className="act-mobile-summary-card">
          <div className="act-mobile-summary-price">${summaryStats.avgPrice.toLocaleString()}</div>
          <div className="act-mobile-summary-left">
            <div className="act-mobile-summary-title">SOLD LISTINGS</div>
            <div className="act-mobile-summary-count">{summaryStats.soldListings} Listings in Radius</div>
          </div>
        </div>
        <div className="act-mobile-comps-list">
          {filteredComps.length > 0 ? (
            filteredComps.map((comp) => (
              <CompRowCard
                key={comp.id}
                comp={comp}
                isSelected={selectedComp?.id === comp.id}
                onClick={() => handleCompSelect(comp)}
                isMobile={true}
                compactHorizontal={true}
              />
            ))
          ) : (
            <div className="no-comps-message">No comparable vehicles found matching your criteria.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="active-comps-tab">
      <div className="act-grid">
        {/* Left Column (8 cols) */}
        <div className="act-left-column">
          {/* Time Period Filter */}
          <div className="ms-period-filter">
            <div className="ms-period-label">Time Period:</div>
            <div className="ms-period-buttons">
              <button
                className={`ms-period-button ${selectedPeriod === "30D" ? "active" : ""}`}
                onClick={() => setSelectedPeriod("30D")}
              >
                30D
              </button>
              <button
                className={`ms-period-button ${selectedPeriod === "60D" ? "active" : ""}`}
                onClick={() => setSelectedPeriod("60D")}
              >
                60D
              </button>
              <button
                className={`ms-period-button ${selectedPeriod === "90D" ? "active" : ""}`}
                onClick={() => setSelectedPeriod("90D")}
              >
                90D
              </button>
            </div>
          </div>

          {/* Market Control Bar */}
          <MarketControlBar onFilterChange={handleFilterChange} initialFilters={filters} hooptieMileage={hooptieMileage} isSoldComps={true} />

          {/* Summary Stats */}
          <SummaryStatsRow stats={summaryStats} isSold={true} />

          {/* All Comps in One Card */}
          <div className="act-comps-card">
            {filteredComps.length > 0 ? (
              filteredComps.map((comp) => (
                <CompRowCard
                  key={comp.id}
                  comp={comp}
                  isSelected={selectedComp?.id === comp.id}
                  onClick={() => handleCompSelect(comp)}
                />
              ))
            ) : (
              <div className="no-comps-message">No comparable vehicles found matching your criteria.</div>
            )}
          </div>
        </div>

        {/* Right Column (4 cols) */}
        <div className="act-right-column">
          <VehicleSummaryCard vehicle={selectedComp} data={data} />
        </div>
      </div>
    </div>
  );
}

